import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import ReactPlayer from "react-player";
import refreshFileUrl from "../utils/refreshFileUrl";
import {
  MediaController,
  MediaControlBar,
  MediaTimeDisplay,
  MediaVolumeRange,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaFullscreenButton,
} from "media-chrome/react";

const Video = (props) => {
  const { lesson, courseId, handleLessonCompletion, isLastLesson } = props;

  const [isFinished, setIsFinished] = useState(false);
  const [fileUrl, setFileUrl] = useState(lesson.file_url);
  const [loadError, setLoadError] = useState(false);
  const playerRef = useRef(null);
  const retriedRef = useRef(false);
  const positionRef = useRef(0);

  useEffect(() => {
    setFileUrl(lesson.file_url);
    setLoadError(false);
    retriedRef.current = false;
    positionRef.current = 0;
  }, [lesson.id, lesson.file_url]);

  // The URL expired mid-playback: fetch a fresh one and resume at the same second, so the
  // learner never sees the video restart.
  const handlePlayerError = async () => {
    if (retriedRef.current) {
      setLoadError(true);
      return;
    }

    retriedRef.current = true;

    const refreshed = await refreshFileUrl(courseId, lesson.id);

    if (refreshed) {
      setFileUrl(refreshed);
    } else {
      setLoadError(true);
    }
  };

  const handleTimeUpdate = (event) => {
    positionRef.current = event.target.currentTime;
  };

  const handleReady = () => {
    if (positionRef.current > 0 && playerRef.current) {
      playerRef.current.currentTime = positionRef.current;
    }
  };

  const handleKeyDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleNextLesson = async () => {
    await handleLessonCompletion();
  };

  return (
    <div className="w-full">
      {loadError && (
        <div className="mb-3 px-4 py-3 bg-gray-100 text-sm text-gray-700">
          Nie udało się wczytać materiału. Odśwież stronę, aby kontynuować.
        </div>
      )}
      <div className="w-full">
        <MediaController
          autohide={3}
          nohotkeys
          className="relative w-full"
          style={{
            width: "100%",
            aspectRatio: "16/9",
          }}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          <ReactPlayer
            ref={playerRef}
            slot="media"
            src={fileUrl}
            onError={handlePlayerError}
            onTimeUpdate={handleTimeUpdate}
            onReady={handleReady}
            className="absolute inset-0 w-full"
            style={{
              height: "100%",
              width: "100%",
              aspectRatio: "16/9",
              "--controls": "none",
            }}
            controls={false}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            onEnded={() => setIsFinished(true)}
          />
          <MediaControlBar
            className="
            absolute bottom-0 left-0 right-0
            flex items-center justify-between gap-2
            px-4 py-3
            bg-gradient-to-t from-black/80 via-black/60 to-transparent
            backdrop-blur-sm
            transition-all duration-300 ease-in-out
            hover:from-black/90 hover:via-black/70
          "
          >
            <div className="flex items-center gap-2">
              <MediaPlayButton
                className="
                w-10 h-10 flex items-center justify-center
                text-white hover:text-primaryYellow
                transition-colors duration-200
                hover:bg-white/10
              "
              />
              <MediaSeekBackwardButton
                seekOffset={30}
                className="
                  w-8 h-8 flex items-center justify-center
                  text-white hover:text-primaryYellow
                  transition-colors duration-200
                  hover:bg-white/10
                "
              />
            </div>

            <div className="flex-1 mx-4">
              <MediaTimeDisplay
                showDuration
                className="
                  text-white text-sm font-medium
                  bg-black/30 px-2 py-1
                  backdrop-blur-sm
                "
              />
            </div>

            <div className="flex items-center gap-2">
              <MediaVolumeRange
                className="
                w-20 h-1
                accent-primaryYellow
                [&::-webkit-slider-track]:bg-white/30
                [&::-webkit-slider-thumb]:bg-primaryYellow
                [&::-webkit-slider-thumb]:border-0
                [&::-moz-range-track]:bg-white/30
                [&::-moz-range-thumb]:bg-primaryYellow
                [&::-moz-range-thumb]:border-0
              "
              />
              <MediaFullscreenButton
                className="
                w-8 h-8 flex items-center justify-center
                text-white hover:text-primaryYellow
                transition-colors duration-200
                hover:bg-white/10
              "
              />
            </div>
          </MediaControlBar>
        </MediaController>
      </div>

      <div className="flex justify-end mt-4">
        <button
          onClick={handleNextLesson}
          disabled={!isFinished}
          className="
            px-6 py-2 bg-black text-white text-sm
            hover:bg-primaryYellow
            disabled:bg-gray-200 disabled:text-gray-400
            enabled:cursor-pointer
            transition-colors
          "
        >
          {isLastLesson ? "Zakończ kurs" : "Następna lekcja"}
        </button>
      </div>

      <div className="mt-4 mb-6 flex flex-col gap-3 w-full">
        <span className="text-gray-600 text-base leading-relaxed block">
          {lesson.description}
        </span>
      </div>
    </div>
  );
};

Video.propTypes = {
  courseId: PropTypes.string,
  lesson: PropTypes.object,
  handleLessonCompletion: PropTypes.func,
  isLastLesson: PropTypes.bool,
};

export default Video;
